import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import "@/pages/ph360/wp-admin.css";

import TabImport from "@/pages/ph360/tabs/TabImport";
import TabBook from "@/pages/ph360/tabs/TabBook";
import TabFront from "@/pages/ph360/tabs/TabFront";
import TabLayout from "@/pages/ph360/tabs/TabLayout";
import TabBack from "@/pages/ph360/tabs/TabBack";
import TabSmartFlow from "@/pages/ph360/tabs/TabSmartFlow";
import TabIntegrity from "@/pages/ph360/tabs/TabIntegrity";
import TabPreview from "@/pages/ph360/tabs/TabPreview";
import TabEditor from "@/pages/ph360/tabs/TabEditor";
import TabChapters from "@/pages/ph360/tabs/TabChapters";
import TabAdmin from "@/pages/ph360/tabs/TabAdmin";
import ProjectSwitcher from "@/pages/ph360/ProjectSwitcher";

/** PH360 — Web port of PublishingHub360 eBook Formatter 360 (wp-admin shell). */

const TABS = [
  { key: "import", label: "Import" },
  { key: "book", label: "Book Info" },
  { key: "chapters", label: "Chapters" },
  { key: "front", label: "Front Matter" },
  { key: "layout", label: "Layout" },
  { key: "back", label: "Back Matter" },
  { key: "smart", label: "Smart Flow" },
  { key: "integrity", label: "Integrity Check" },
  { key: "preview", label: "Preview / Export" },
  { key: "editor", label: "Final Editor" },
];

const ADMIN_TAB = { key: "admin", label: "Admin" };

export default function PH360() {
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const tab = params.get("tab") || "import";
  const [project, setProject] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const setTab = (t) => setParams({ tab: t });

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([api.get("/ph360/project"), api.get("/ph360/meta")]);
      setProject(p.data);
      setMeta(m.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !project || !meta) {
    return <div className="wp-wrap"><div className="wp-main">Loading…</div></div>;
  }

  return (
    <div className="wp-wrap ef360">
      <div className="wp-topbar" data-testid="ph360-topbar">
        <Link to="/dashboard" style={{ marginRight: 18 }}>← BookForge</Link>
        <strong style={{ marginRight: 12 }}>eBook Formatter 360</strong>
        <span style={{ color: "#8c8f94" }}>PublishingHub360</span>
        <span style={{ marginLeft: "auto", color: "#c3c4c7", fontSize: 12 }}>
          {user?.email} · <a href="#" onClick={(e)=>{e.preventDefault(); logout().then(()=>nav("/"));}} data-testid="ph360-logout">Log out</a>
        </span>
      </div>
      <div className="wp-shell">
        <aside className="wp-sidebar" data-testid="ph360-sidebar">
          <a href="#" className="active">📖 eBook Formatter 360</a>
          <ProjectSwitcher activeId={project?.id} onSwitch={() => load()} />
        </aside>
        <main className="wp-main">
          <h1 className="wp-title">
            eBook Formatter 360 <span>v{meta.version} · {meta.codename}</span>
          </h1>
          <div className="notice notice-info">
            <p><strong>Current project:</strong> {(project.book_info?.title || "Untitled Project")} · Personal workspace for {user?.name || user?.email}.</p>
          </div>

          <div className="nav-tab-wrapper" data-testid="ph360-tabs">
            {[...TABS, ...(((user?.role || "").toLowerCase() === "admin") ? [ADMIN_TAB] : [])].map((t) => (
              <a
                key={t.key}
                className={`nav-tab ${tab === t.key ? "nav-tab-active" : ""}`}
                href="#"
                onClick={(e)=>{e.preventDefault(); setTab(t.key);}}
                data-testid={`ph360-tab-${t.key}`}
              >
                {t.label}
              </a>
            ))}
          </div>

          <div className="ef360-card">
            {tab === "import"    && <TabImport project={project} onChange={setProject} />}
            {tab === "book"      && <TabBook project={project} onChange={setProject} />}
            {tab === "chapters"  && <TabChapters project={project} onChange={setProject} />}
            {tab === "front"     && <TabFront project={project} onChange={setProject} />}
            {tab === "layout"    && <TabLayout project={project} meta={meta} onChange={setProject} />}
            {tab === "back"      && <TabBack project={project} onChange={setProject} />}
            {tab === "smart"     && <TabSmartFlow project={project} onChange={setProject} />}
            {tab === "integrity" && <TabIntegrity project={project} reload={load} />}
            {tab === "preview"   && <TabPreview project={project} />}
            {tab === "editor"    && <TabEditor project={project} onChange={setProject} />}
            {tab === "admin" && (user?.role || "").toLowerCase() === "admin" && <TabAdmin />}
          </div>
        </main>
      </div>
    </div>
  );
}
